import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {TtdDataRate} from './ttd-data-rate.types';
import {TtdRateType} from '../../target/target.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-ttd-data-rate',
  template: `
    <div [class.edit-box]="editMode" class="box" [ngClass]="{'always-open':initEditMode}">
      <!-- Display Mode -->
      <div *ngIf="!editMode" class="view">

        <!-- Display Available Data -->
        <div *ngIf="rate" class="view-rate">
          <button (click)="editMode = true" mat-button>
            <div class="button-text">
              <div class="display-hint" *ngIf="displayHint">{{ displayHint }}</div>
              <span *ngIf="rate?.rateType === 'CPM'">{{ rate?.cpmAmountUsd | currency }}</span>
              <span *ngIf="rate?.rateType === 'Hybrid'">
          {{ rate?.percentOfMediaCostRate | number }}% / {{ rate?.cpmAmountUsd | currency }}
        </span>
              <span
                *ngIf="rate?.rateType === 'PercentOfMediaCost'">{{ rate?.percentOfMediaCostRate | number }}
                %</span>
            </div>
          </button>
        </div>

        <!-- Display No Data Button -->
        <div *ngIf="!rate" class="view-rate-missing">
          <button (click)="edit()" mat-icon-button>
            <mat-icon>add_box</mat-icon>
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <div *ngIf="editMode" class="edit">
        <div class="edit-form">
          <div class="input-field" style="margin-bottom: -1.25em">
            <mat-form-field appearance="outline"
                            class="no-padding-bottom type-select"
                            data-cy="input-date-rate-select">
              <mat-select [formControl]="rateTypeControl" required>
                <mat-option value="CPM">CPM</mat-option>
                <mat-option value="Hybrid">Hybrid</mat-option>
                <mat-option value="PercentOfMediaCost">PercentOfMediaCost</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div *ngIf="isCpm() || isHybrid()" class="amount-field">
            <mat-form-field appearance="outline">
              <mat-label *ngIf="isCpm()">CPM $</mat-label>
              <mat-label *ngIf="isHybrid()">Cap $</mat-label>
              <input [formControl]="amountControl" class="amount" matInput placeholder="0.00"
                     required step="0.01"
                     type="number">
              <mat-hint>The flat CPM fee or CAP in USD</mat-hint>
            </mat-form-field>
          </div>
          <div *ngIf="isPercentOfMediaCost() || isHybrid()" class="percent-field">
            <mat-form-field appearance="outline">
              <mat-label>% / MediaCost</mat-label>
              <input [formControl]="percentControl" class="percent" matInput required pattern="\\d*" step="1"
                     type="text"
                     autocomplete="off">
              <mat-hint>The % of media cost - e.g. 1%</mat-hint>
            </mat-form-field>
          </div>
        </div>
        <div class="edit-buttons">
          <button (click)="submit()" [disabled]="!isValid()" mat-icon-button name="submit" title="Enter">
            <mat-icon>check</mat-icon>
          </button>
          <button (click)="close()" *ngIf="!initEditMode" mat-icon-button title="Esc">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styleUrls: ['./ttd-data-rate.component.scss']
})
export class TtdDataRateComponent implements OnInit, OnDestroy {

  @Input() rate?: TtdDataRate;

  @Input() initEditMode?: boolean;

  editMode = false;

  rateTypeControl = new FormControl(TtdRateType.CPM, [Validators.required]);

  amountControl = new FormControl(null, [Validators.required, Validators.min(0)]);

  percentControl = new FormControl(null, [Validators.required, Validators.min(0), Validators.pattern(/\d+/)]);

  @Output() out = new EventEmitter<TtdDataRate>();

  subscription = new Subscription();

  @Input() displayHint!: string;

  constructor() {
  }

  ngOnInit(): void {
    if (this.rate) {
      this.rateTypeControl.patchValue(this.rate.rateType);
      this.amountControl.patchValue(this.rate.cpmAmountUsd);
      this.percentControl.patchValue(this.rate.percentOfMediaCostRate);
    }

    if (this.initEditMode) {
      this.editMode = true;
    }

  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getValue(): TtdDataRate {
    return {
      rateType: this.rateTypeControl.value,
      cpmAmountUsd: this.amountControl.value,
      percentOfMediaCostRate: this.percentControl.value
    };
  }

  edit(): void {
    this.editMode = true;
  }

  close(): void {
    this.editMode = false;
  }

  isValid(): boolean {
    if (this.rateTypeControl.value === TtdRateType.CPM) {
      return this.amountControl.valid;
    }
    if (this.rateTypeControl.value === TtdRateType.PercentOfMediaCost) {
      return this.percentControl.valid;
    }
    return this.amountControl.valid && this.percentControl.valid;
  }

  submit(): void {
    // build the arte for submit
    this.rate = this.getValue();
    if (this.rate.rateType === TtdRateType.CPM) {
      delete this.rate.percentOfMediaCostRate;
    }
    if (this.rate.rateType === TtdRateType.PercentOfMediaCost) {
      delete this.rate.cpmAmountUsd;
    }

    this.out.emit(this.rate);
    if (!this.initEditMode) {
      this.close();
    }

  }

  isCpm(): boolean {
    return this.rateTypeControl.value === TtdRateType.CPM;
  }

  isHybrid(): boolean {
    return this.rateTypeControl.value === TtdRateType.Hybrid;
  }

  isPercentOfMediaCost(): boolean {
    return this.rateTypeControl.value === TtdRateType.PercentOfMediaCost;
  }

  @HostListener('keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {

    if (this.editMode && event.code === 'Enter' && this.isValid()) {
      this.submit();
    }

    if (this.editMode && event.code === 'Escape') {
      this.close();
    }
  }

}
